import { useRef } from 'react';
import styles from './OtpInput.module.css';

/**
 * Six-box one-time-code entry, as used by the email login.
 *
 * Controlled: hand it `value` (a string of digits) and `onChange`.
 * `onComplete` fires once the last digit is filled, so the caller can submit
 * without the user having to click a button.
 *
 * Handles the details people expect: auto-advance, backspace stepping back,
 * arrow keys, and pasting the whole code into any box.
 */
export function OtpInput({
  value = '',
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
}) {
  const refs = useRef([]);
  const digits = value.padEnd(length, ' ').slice(0, length).split('');

  const commit = (next) => {
    const clean = next.slice(0, length);
    onChange?.(clean);
    if (clean.length === length && !clean.includes(' ')) onComplete?.(clean);
  };

  const focusBox = (i) => refs.current[i]?.focus();

  const handleInput = (i, raw) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) return;

    // Typing several digits at once (or a paste landing here) fills forward.
    const chars = value.split('');
    for (let k = 0; k < typed.length && i + k < length; k++) {
      chars[i + k] = typed[k];
    }
    const next = chars.join('').replace(/\s/g, '').slice(0, length);
    commit(next);
    focusBox(Math.min(i + typed.length, length - 1));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const chars = value.split('');
      if (chars[i]) {
        chars[i] = '';
        commit(chars.join('').replace(/\s/g, ''));
      } else if (i > 0) {
        chars[i - 1] = '';
        commit(chars.join('').replace(/\s/g, ''));
        focusBox(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    commit(pasted);
    focusBox(Math.min(pasted.length, length - 1));
  };

  return (
    <div className={styles.otp} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={`${styles.box} ${invalid ? styles.invalid : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={d.trim()}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`Digit ${i + 1} of ${length}`}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
